import { useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, LoadingState, Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage, fmtDate } from "@/lib/api";

const TONE = { extracted: "emerald", no_text: "amber", failed: "rose" };

export function DocumentsDialog({ product, onOpenChange }) {
  const { api, can } = useWorkspace();
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { data, loading, error, reload } = useFetch(product ? "/documents" : null, { product_profile_id: product?.id });

  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file); form.append("purpose", "product_document"); form.append("product_profile_id", product.id);
    setUploading(true);
    try { const { data: d } = await api.post("/documents", form); toast.success(`${d.filename} uploaded (${d.extraction_status})`); reload(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setUploading(false); if (input.current) input.current.value = ""; }
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: d.filename }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <Dialog open={Boolean(product)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="documents-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Documents · {product?.name}</DialogTitle>
          <DialogDescription>Private files (PDF, DOCX, TXT, MD, CSV, up to 15 MB). Text is extracted to guide research and qualification.</DialogDescription>
        </DialogHeader>
        {can("write") && (
          <div>
            <input ref={input} type="file" accept=".pdf,.docx,.txt,.md,.csv" className="hidden" data-testid="document-file-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            <Button onClick={() => input.current?.click()} disabled={uploading} data-testid="document-upload-button">{uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Upload document</Button>
          </div>
        )}
        {loading && !data && <LoadingState />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.length && <EmptyState title="No documents yet" testid="documents-empty" />}
        <div className="max-h-[45vh] space-y-2 overflow-y-auto">
          {data?.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-md border p-3 text-sm" data-testid={`document-${d.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{d.filename}</p>
                <p className="text-xs text-muted-foreground">{d.size < 1024 ? `${d.size} B` : `${(d.size / 1024).toFixed(0)} KB`} · {fmtDate(d.created_at)} · {d.text_chars.toLocaleString()} characters extracted{d.extraction_error ? ` · ${d.extraction_error}` : ""}</p>
              </div>
              <Pill tone={TONE[d.extraction_status]}>{d.extraction_status.replace("_", " ")}</Pill>
              <Button size="icon" variant="ghost" onClick={() => download(d)} data-testid={`document-download-${d.id}`}><Download className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
